import React, { useState, useMemo } from 'react'
import SuperUserAccountPageUI from '../../../templates/SuperUserAccountPageUI'
import GenericErrorUI from '../../../components/common/GenericErrorUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'

// ─── helpers ────────────────────────────────────────────────────────────────

// The API sends amounts as Indian-formatted strings, e.g. "22,00,000.00".
// Plain Number(...) on that returns NaN. Strip thousands separators before
// parsing, and tolerate numbers that already came in clean.
const parseAmount = (amount) => {
  if (amount == null) return 0
  if (typeof amount === 'number') return amount
  const cleaned = String(amount).replace(/,/g, '').trim()
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : 0
}

const formatAmount = (amount) => {
  if (amount == null) return '—'
  return parseAmount(amount).toLocaleString('en-IN')
}

const formatSignedAmount = (amount) => {
  if (amount == null) return '—'
  const n = parseAmount(amount)
  const sign = n > 0 ? '+' : ''
  return sign + n.toLocaleString('en-IN')
}

const formatSignedPercent = (val) => {
  if (val == null || !Number.isFinite(Number(val))) return '—'
  const n = Number(val)
  const sign = n > 0 ? '+' : ''
  return sign + n.toFixed(0) + '%'
}

const monthLabel = (yearMonth) => {
  if (!yearMonth) return '—'
  const [y, m] = yearMonth.split('-')
  if (!y || !m) return yearMonth
  const date = new Date(Number(y), Number(m) - 1, 1)
  return date.toLocaleString('en-US', { month: 'short' }) + '-' + y.slice(2)
}

const statusBadge = (status) => {
  if (!status) return null
  const isComplied = status === 'complied'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        background: isComplied ? '#e6f9f0' : '#fff0f0',
        color: isComplied ? '#1a7f4f' : '#c0392b',
        border: '1px solid ' + (isComplied ? '#a3e4c1' : '#f5b7b1'),
      }}
    >
      {isComplied ? 'Complied' : 'Not Complied'}
    </span>
  )
}

// Normalise a raw monthly row into the shape the table renders. Variation is
// always derived from expected/actual so it stays correct regardless of
// whether the API sends "shortfall" (positive = deficit) or nothing at all.
const withVariation = (row) => {
  const expected = parseAmount(row.expectedInflow)
  const actual = parseAmount(row.actualInflow)
  const variance = actual - expected
  const variancePercent = expected !== 0 ? (variance / expected) * 100 : 0
  return { ...row, expectedInflow: expected, actualInflow: actual, variance, variancePercent }
}

const inRange = (yearMonth, from, to) => {
  if (!yearMonth) return false
  if (from && yearMonth < from) return false
  if (to && yearMonth > to) return false
  return true
}

const sumRows = (rows) => {
  return rows.reduce(
    function (acc, r) {
      acc.expected += parseAmount(r.expectedInflow)
      acc.actual += parseAmount(r.actualInflow)
      return acc
    },
    { expected: 0, actual: 0 }
  )
}

// Worst-case status across a set of monthly rows: any non-compliant month
// makes the whole (filtered) loan "not_complied".
const overallStatusOf = (rows) => {
  if (!rows || rows.length === 0) return null
  const hasNonComplied = rows.some(function (r) { return r.status === 'not_complied' })
  return hasNonComplied ? 'not_complied' : 'complied'
}

// ─── API response adapter ───────────────────────────────────────────────────
//
// The backend returns one flat object: { accountSummary, loanDetails: [...] }
// where each loanDetails row is a single loan's month, using field names
// like nonComplianceText / shortfallPercent instead of what the UI expects.
// This normalises each loan row (and parses the comma-formatted amounts) into
// the flat loanDetails shape the page component takes.
//
// Call this once, right after you fetch the API response, then pass the
// result into <ScheduleVIISummaryPageUI loanDetails={...} />.
export const mapScheduleViiApiResponse = (apiResponse) => {
  const rawLoanDetails = (apiResponse && apiResponse.loanDetails) || []

  const loanDetails = rawLoanDetails.map(function (row) {
    return {
      loanId: row.loanId,
      loanName: row.loanName,
      loanReferenceNumber: row.loanReferenceNumber,
      loanType: row.loanType,
      counterpartyName: row.counterpartyName || null,
      yearMonth: row.yearMonth,
      expectedInflow: parseAmount(row.expectedInflow),
      actualInflow: parseAmount(row.actualInflow),
      status: row.status,
    }
  })

  return { loanDetails }
}

// ─── Monthly breakdown table (rendered inside an expanded loan row) ────────

const MonthlyBreakdownTable = ({ monthlyBreakdown }) => {
  if (!monthlyBreakdown || monthlyBreakdown.length === 0) {
    return (
      <div style={{ padding: '16px 0', color: '#888', fontSize: 14 }}>
        No history available for this period.
      </div>
    )
  }

  const totals = sumRows(monthlyBreakdown)
  const totalVariance = totals.actual - totals.expected
  const totalVariancePercent = totals.expected !== 0 ? (totalVariance / totals.expected) * 100 : 0

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f7f8fa', borderBottom: '2px solid #e0e0e0' }}>
            {['Months', 'Expected Inflow', 'Actual Inflows', 'Variation', 'Variation (%)'].map(function (h) {
              return (
                <th key={h} style={{
                  padding: '10px 12px', textAlign: 'left',
                  fontWeight: 600, color: '#555', whiteSpace: 'nowrap'
                }}>
                  {h}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {monthlyBreakdown.map(function (row, idx) {
            const isNegative = row.variance < 0
            return (
              <tr
                key={row.yearMonth || idx}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  background: idx % 2 === 0 ? '#fff' : '#fafafa',
                }}
              >
                <td style={{ padding: '10px 12px', fontWeight: 500, color: '#222' }}>
                  {monthLabel(row.yearMonth)}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>
                  {formatAmount(row.expectedInflow)}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>
                  {formatAmount(row.actualInflow)}
                </td>
                <td style={{
                  padding: '10px 12px',
                  color: isNegative ? '#c0392b' : '#1a7f4f',
                  fontWeight: 600
                }}>
                  {formatSignedAmount(row.variance)}
                </td>
                <td style={{
                  padding: '10px 12px',
                  color: isNegative ? '#c0392b' : '#1a7f4f',
                  fontWeight: 600
                }}>
                  {formatSignedPercent(row.variancePercent)}
                </td>
              </tr>
            )
          })}

          {/* ── Total row ── */}
          <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f7f8fa' }}>
            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#222' }}>Total</td>
            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#222' }}>
              {formatAmount(totals.expected)}
            </td>
            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#222' }}>
              {formatAmount(totals.actual)}
            </td>
            <td style={{
              padding: '10px 12px', fontWeight: 700,
              color: totalVariance < 0 ? '#c0392b' : '#1a7f4f'
            }}>
              {formatSignedAmount(totalVariance)}
            </td>
            <td style={{
              padding: '10px 12px', fontWeight: 700,
              color: totalVariance < 0 ? '#c0392b' : '#1a7f4f'
            }}>
              {formatSignedPercent(totalVariancePercent)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ─── LRN accordion row — click the LRN to expand/collapse its table ───────

const LoanAccordionRow = ({ loanGroup, isExpanded, onToggle }) => {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      marginBottom: 12,
      overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        role='button'
        tabIndex={0}
        onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') onToggle() }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 20px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{
            display: 'inline-block',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
            color: '#888',
            fontSize: 12,
          }}>
            ▶
          </span>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#222' }}>
            {loanGroup.loanReferenceNumber || '—'}
          </span>
          <span style={{ fontSize: 13, color: '#888' }}>
            {loanGroup.loanName || '—'}
            {loanGroup.loanType ? ' · ' + loanGroup.loanType : ''}
            {loanGroup.counterpartyName ? ' · ' + loanGroup.counterpartyName : ''}
          </span>
        </div>
        <div>{statusBadge(loanGroup.currentStatus)}</div>
      </div>

      {isExpanded &&
        <div style={{ padding: '0 20px 16px 20px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ paddingTop: 12 }}>
            <MonthlyBreakdownTable monthlyBreakdown={loanGroup.monthlyBreakdown} />
          </div>
        </div>
      }
    </div>
  )
}

// ─── Main Page UI ────────────────────────────────────────────────────────────

const ScheduleVIISummaryPageUI = ({
  context, errorList, account,
  loanDetails, // flat: [{ loanId, loanName, loanReferenceNumber, loanType, counterpartyName, yearMonth, expectedInflow, actualInflow, status }, ...]
  loading, onRefreshClick
}) => {
  // ── which LRNs are currently expanded ──
  const [expandedLoanKeys, setExpandedLoanKeys] = useState(function () { return new Set() })

  const toggleLoan = (key) => {
    setExpandedLoanKeys(function (prev) {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // ── derive available month range from the data itself ──
  const allYearMonths = useMemo(function () {
    const months = new Set()
    ;(loanDetails || []).forEach(function (r) { if (r.yearMonth) months.add(r.yearMonth) })
    return Array.from(months).sort()
  }, [loanDetails])

  const minYearMonth = allYearMonths[0] || null
  const maxYearMonth = allYearMonths[allYearMonths.length - 1] || null

  const [periodFrom, setPeriodFrom] = useState(null) // null = inception
  const [periodTo, setPeriodTo] = useState(null)     // null = latest (YTD)

  const effectiveFrom = periodFrom || minYearMonth
  const effectiveTo = periodTo || maxYearMonth

  // ── group by LRN + counterparty, then filter + normalise each group ──
  const loanGroups = useMemo(function () {
    const groups = {}
    ;(loanDetails || []).forEach(function (row) {
      const key = (row.loanReferenceNumber || row.loanId) + '|' + (row.counterpartyName || '')
      if (!groups[key]) {
        groups[key] = {
          key: key,
          loanId: row.loanId,
          loanName: row.loanName,
          loanReferenceNumber: row.loanReferenceNumber,
          loanType: row.loanType,
          counterpartyName: row.counterpartyName,
          rows: [],
        }
      }
      groups[key].rows.push(row)
    })

    return Object.keys(groups).map(function (key) {
      const g = groups[key]
      const filteredRows = g.rows
        .filter(function (r) { return inRange(r.yearMonth, effectiveFrom, effectiveTo) })
        .map(withVariation)
        .sort(function (a, b) { return a.yearMonth < b.yearMonth ? -1 : 1 })
      return {
        key: g.key,
        loanId: g.loanId,
        loanName: g.loanName,
        loanReferenceNumber: g.loanReferenceNumber,
        loanType: g.loanType,
        counterpartyName: g.counterpartyName,
        monthlyBreakdown: filteredRows,
        currentStatus: overallStatusOf(filteredRows),
      }
    })
  }, [loanDetails, effectiveFrom, effectiveTo])

  return (
    <SuperUserAccountPageUI
      context={context}
      className='pg_su_account_schedule7'
      backLink='/su/accounts'
      headerText={account ? account.accountName : 'Account'}
      subHeaderText='Schedule VII Compliance Summary'
      account={account}
      page='schedukeVIISummary'
      dimension='w1200'
    >

      {/* ── Header row: refresh icon + title ── */}
      <span className='g_flex_left g_flex_h'>
        <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} label='Refresh Page' />
        <span className='g_m_left_100 g_large'>
          <b>Schedule VII Summary, {monthLabel(effectiveFrom)} to {monthLabel(effectiveTo)}</b>
        </span>
      </span>

      {/* ── Period filter (applies to every loan's table) ── */}
      <div className='g_flex_h g_m_top_20' style={{ marginBottom: 20, alignItems: 'center', gap: 10 }}>
        <span className='g_muted' style={{ lineHeight: '32px' }}>Period</span>
        <input
          type='month'
          value={effectiveFrom || ''}
          min={minYearMonth || undefined}
          max={effectiveTo || maxYearMonth || undefined}
          onChange={function (e) { setPeriodFrom(e.target.value) }}
          style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
        />
        <span className='g_muted'>to</span>
        <input
          type='month'
          value={effectiveTo || ''}
          min={effectiveFrom || minYearMonth || undefined}
          max={maxYearMonth || undefined}
          onChange={function (e) { setPeriodTo(e.target.value) }}
          style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
        />
        {(periodFrom || periodTo) &&
          <button
            className='g_btn_secondary'
            onClick={function () { setPeriodFrom(null); setPeriodTo(null) }}
          >
            Reset to inception–YTD
          </button>
        }
      </div>

      {/* ── Errors ── */}
      {errorList && errorList.length > 0 &&
        <GenericErrorUI error={errorList[0]} />
      }

      {/* ── Loading ── */}
      {loading &&
        <SpinnerComponentUI />
      }

      {/* ── No history at all state ── */}
      {!loading && loanGroups.length === 0 &&
        <div style={{
          background: '#f7f8fa',
          border: '1px solid #e0e0e0',
          borderRadius: 8,
          padding: '16px 20px',
          color: '#555',
          fontSize: 14,
          marginBottom: 16,
        }}>
          No Schedule VII history recorded for any loan up to this period.
        </div>
      }

      {/* ── LRN list — click a row to expand its monthly table ── */}
      {!loading && loanGroups.length > 0 &&
        <div>
          <span className='g_muted g_small' style={{ display: 'block', marginBottom: 8 }}>
            {loanGroups.length} loan{loanGroups.length !== 1 ? 's' : ''} · click an LRN to view its breakdown
          </span>
          {loanGroups.map(function (loanGroup) {
            return (
              <LoanAccordionRow
                key={loanGroup.key}
                loanGroup={loanGroup}
                isExpanded={expandedLoanKeys.has(loanGroup.key)}
                onToggle={function () { toggleLoan(loanGroup.key) }}
              />
            )
          })}
        </div>
      }

    </SuperUserAccountPageUI>
  )
}

export default ScheduleVIISummaryPageUI